"use client";
import React, { useState, useEffect, Fragment, useRef } from "react";
import { PrismicNextImage } from "@prismicio/next";
import { Dialog, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

function ResetOnImageChange({ index, reset }) {
  useEffect(() => {
    reset();
  }, [index, reset]);
  return null;
}

const MasonryGallery = ({ images }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isModalOpen) {
        if (event.key === "ArrowRight") {
          nextImage();
        } else if (event.key === "ArrowLeft") {
          prevImage();
        } else if (event.key === "Escape") {
          closeModal();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const columns = 4; // Number of columns in the masonry layout
  const columnWrapper = {};
  const result = [];

  // Create column arrays
  for (let i = 0; i < columns; i++) {
    columnWrapper[`column${i}`] = [];
  }

  // Distribute images into columns
  for (let i = 0; i < images.length; i++) {
    const columnIndex = i % columns;
    columnWrapper[`column${columnIndex}`].push({ ...images[i], index: i });
  }

  // Wrap column content in divs
  for (let i = 0; i < columns; i++) {
    result.push(
      <div key={i} className="grid gap-4">
        {columnWrapper[`column${i}`].map((imageObject) => (
          <div
            key={imageObject.index}
            className="cursor-pointer flex transform transition-transform duration-300 hover:scale-105"
            onClick={() => openModal(imageObject.index)}
            aria-label={`Open image ${imageObject.index + 1}`}
          >
            <PrismicNextImage
              field={imageObject.image}
              alt="Image Gallery"
              className="h-auto max-w-full rounded-lg"
            />
          </div>
        ))}
      </div>
    );
  }

  // Thumbnail strip helpers
  const thumbRefs = useRef([]);
  useEffect(() => {
    if (!isModalOpen) return;
    const el = thumbRefs.current[currentImageIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [currentImageIndex, isModalOpen]);

  return (
    <div>
      {/* Masonry Gallery */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ">{result}</div>

      {/* Fullscreen lightbox */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          open={isModalOpen}
          onClose={closeModal}
          className="fixed inset-0 z-[99999]"
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/90"
              aria-hidden="true"
              onClick={closeModal}
            />
          </Transition.Child>

          {/* Lightbox content */}
          <div className="fixed inset-0 flex flex-col">
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white z-50"
              aria-label="Close"
            >
              <Icon icon="mingcute:close-fill" className="w-7 h-7" />
            </button>

            {/* Main image area */}
            <div className="flex-1 flex items-center justify-center px-4 md:px-12 pt-10 pb-2">
              <div className="relative w-full h-full flex items-center justify-center">
                <TransformWrapper
                  key={currentImageIndex}
                  initialScale={1}
                  minScale={1}
                  maxScale={5}
                  doubleClick={{ disabled: false, mode: "zoomIn" }}
                  pinch={{ step: 5 }}
                  wheel={{ step: 0.15, touchPadEnabled: true }}
                  panning={{ velocityDisabled: true }}
                  limitToBounds
                  centerOnInit
                >
                  {({ resetTransform, state }) => (
                    <div className="relative w-full h-auto flex items-center justify-center touch-none select-none">
                      <ResetOnImageChange
                        index={currentImageIndex}
                        reset={resetTransform}
                      />
                      <TransformComponent
                        wrapperStyle={{ width: "100%", height: "60vh" }}
                      >
                        <PrismicNextImage
                          field={images[currentImageIndex].image}
                          alt="Image Gallery"
                          className="w-auto h-auto object-contain pointer-events-none"
                          priority
                        />
                      </TransformComponent>
                    </div>
                  )}
                </TransformWrapper>
              </div>
            </div>

            {/* Thumbnail strip */}
            <div className="w-full h-[125px] bg-black/60 backdrop-blur-sm border-t border-white/10">
              <div className="h-full overflow-x-auto overflow-y-hidden whitespace-nowrap no-scrollbar px-4 py-2">
                {images.map((img, i) => {
                  const isActive = i === currentImageIndex;
                  return (
                    <button
                      key={i}
                      ref={(el) => (thumbRefs.current[i] = el)}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`inline-block mr-3 rounded-lg overflow-hidden border ${
                        isActive
                          ? "ring-2 ring-white border-white/80"
                          : "border-white/30 hover:border-white/70"
                      }`}
                      style={{ width: 96, height: 96 }}
                      aria-label={`Go to image ${i + 1}`}
                    >
                      <PrismicNextImage
                        field={img.image}
                        alt={`Thumbnail ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MasonryGallery;
